import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Paperclip, X } from 'lucide-react';
import { ApiError } from '../../lib/apiClient';
import {
  MAX_ATTACHMENT_BYTES,
  MAX_RECIPIENTS,
  MAX_SUBJECT_LENGTH,
  PHI_POLICY_LINE,
  addRecipients,
  parseRecipientInput,
  totalAttachmentBytes,
  validateDraft,
  type ComposeSeed,
} from '../../lib/mailCompose';
import { htmlToPlainText, sanitizeEditorHtml } from '../../lib/mailEditorHtml';
import { formatBytes } from '../../lib/mailFormat';
import { sendMail } from '../../lib/mailApi';
import type { MailMailboxDto, MailSendMode, MailSendResultDto } from '../../lib/mailTypes';
import { MailEditor } from './MailEditor';
import { RecipientField } from './RecipientField';

const TITLES: Record<MailSendMode, string> = {
  new: 'New message',
  reply: 'Reply',
  replyAll: 'Reply all',
  forward: 'Forward',
};

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Compose / reply / forward window: a drawer docked bottom-right on wide screens, full screen on narrow
 * ones. The draft lives only in component state (never persisted, never logged); a failed send keeps it.
 */
export function MailCompose({
  mailboxes,
  seed,
  onClose,
  onSent,
  onDirtyChange,
}: {
  mailboxes: MailMailboxDto[];
  seed: ComposeSeed;
  onClose: () => void;
  onSent: (result: MailSendResultDto) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [mailboxId, setMailboxId] = useState(seed.mailboxId);
  const [to, setTo] = useState(seed.to);
  const [cc, setCc] = useState(seed.cc);
  const [bcc, setBcc] = useState(seed.bcc);
  const [pendingTo, setPendingTo] = useState('');
  const [pendingCc, setPendingCc] = useState('');
  const [pendingBcc, setPendingBcc] = useState('');
  const [showCc, setShowCc] = useState(seed.cc.length > 0);
  const [showBcc, setShowBcc] = useState(seed.bcc.length > 0);
  const [subject, setSubject] = useState(seed.subject);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [bodyEdited, setBodyEdited] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rawHtml = useRef('');
  const fileInput = useRef<HTMLInputElement>(null);

  const dirty =
    bodyEdited ||
    subject !== seed.subject ||
    !sameList(to, seed.to) ||
    !sameList(cc, seed.cc) ||
    !sameList(bcc, seed.bcc) ||
    attachments.length > 0 ||
    pendingTo.trim() !== '' ||
    pendingCc.trim() !== '' ||
    pendingBcc.trim() !== '';

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const attachmentBytes = useMemo(() => totalAttachmentBytes(attachments), [attachments]);
  const overLimit = attachmentBytes > MAX_ATTACHMENT_BYTES;

  const discard = () => {
    if (sending) return;
    if (dirty && !window.confirm('Discard this message? Your draft will be lost.')) return;
    onClose();
  };

  const pickFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (picked.length) setAttachments((prev) => [...prev, ...picked]);
  };

  const send = async () => {
    // Fold text still sitting in a recipient input into its chip list before validating.
    const nextTo = addRecipients(to, parseRecipientInput(pendingTo));
    const nextCc = addRecipients(cc, parseRecipientInput(pendingCc));
    const nextBcc = addRecipients(bcc, parseRecipientInput(pendingBcc));
    setTo(nextTo);
    setCc(nextCc);
    setBcc(nextBcc);
    setPendingTo('');
    setPendingCc('');
    setPendingBcc('');
    if (nextCc.length) setShowCc(true);
    if (nextBcc.length) setShowBcc(true);

    const problem = validateDraft({ mailboxId, to: nextTo, cc: nextCc, bcc: nextBcc, subject, attachments });
    if (problem) {
      setError(problem);
      return;
    }
    // Whatever the editor holds (typed, pasted, or injected) is rebuilt from an allow-list here.
    const html = sanitizeEditorHtml(rawHtml.current);
    const text = htmlToPlainText(html);

    setError(null);
    setSending(true);
    try {
      const result = await sendMail({
        payload: {
          mailboxId,
          mode: seed.mode,
          ...(seed.threadId ? { threadId: seed.threadId } : {}),
          ...(seed.inReplyToMessageId ? { inReplyToMessageId: seed.inReplyToMessageId } : {}),
          to: nextTo,
          ...(nextCc.length ? { cc: nextCc } : {}),
          ...(nextBcc.length ? { bcc: nextBcc } : {}),
          subject: subject.trim(),
          text,
          html,
        },
        attachments,
      });
      onSent(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The message could not be sent. Your draft is kept.');
      setSending(false);
    }
  };

  return (
    <section className="mail-compose" role="dialog" aria-label={TITLES[seed.mode]}>
      <header className="mail-compose-head">
        <h2>{TITLES[seed.mode]}</h2>
        <button type="button" className="btn btn-xs" aria-label="Close" disabled={sending} onClick={discard}>
          <X size={14} aria-hidden />
        </button>
      </header>
      <div className="mail-compose-body">
        <div className="mail-compose-row">
          <label htmlFor="mail-compose-from">From</label>
          <select
            id="mail-compose-from"
            value={mailboxId}
            disabled={sending}
            onChange={(e) => setMailboxId(e.target.value)}
          >
            {mailboxes.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName} &lt;{m.address}&gt;
              </option>
            ))}
          </select>
        </div>
        <fieldset className="mail-compose-fieldset" disabled={sending}>
          <div className="mail-compose-to">
            <RecipientField
              label="To"
              value={to}
              pending={pendingTo}
              onChange={setTo}
              onPendingChange={setPendingTo}
              autoFocus={seed.to.length === 0}
            />
            {(!showCc || !showBcc) && (
              <span className="mail-compose-toggles">
                {!showCc && (
                  <button type="button" className="btn btn-xs" onClick={() => setShowCc(true)}>
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button type="button" className="btn btn-xs" onClick={() => setShowBcc(true)}>
                    Bcc
                  </button>
                )}
              </span>
            )}
          </div>
          {showCc && (
            <RecipientField label="Cc" value={cc} pending={pendingCc} onChange={setCc} onPendingChange={setPendingCc} />
          )}
          {showBcc && (
            <RecipientField
              label="Bcc"
              value={bcc}
              pending={pendingBcc}
              onChange={setBcc}
              onPendingChange={setPendingBcc}
            />
          )}
          <div className="mail-compose-row">
            <label htmlFor="mail-compose-subject">Subject</label>
            <input
              id="mail-compose-subject"
              type="text"
              value={subject}
              autoComplete="off"
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          {subject.length > MAX_SUBJECT_LENGTH && (
            <div className="mail-compose-hint mail-compose-hint-warn">
              Subject is {subject.length - MAX_SUBJECT_LENGTH} characters over the {MAX_SUBJECT_LENGTH} limit.
            </div>
          )}
        </fieldset>
        <MailEditor
          initialText={seed.bodyText}
          disabled={sending}
          autoFocus={seed.to.length > 0}
          onInput={(html, userEdit) => {
            rawHtml.current = html;
            if (userEdit) setBodyEdited(true);
          }}
        />
        {attachments.length > 0 && (
          <div className="mail-compose-attachments">
            <ul aria-label="Attachments">
              {attachments.map((file, i) => (
                <li key={`${file.name}-${i}`} className="mail-compose-file">
                  <Paperclip size={14} aria-hidden />
                  <span className="mail-attachment-name">{file.name}</span>
                  <span className="cell-sub">{formatBytes(file.size)}</span>
                  <button
                    type="button"
                    className="mail-chip-remove"
                    aria-label={`Remove attachment ${file.name}`}
                    disabled={sending}
                    onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                  >
                    <X size={12} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
            <div className={overLimit ? 'mail-compose-hint mail-compose-hint-warn' : 'mail-compose-hint'}>
              {formatBytes(attachmentBytes)} of {formatBytes(MAX_ATTACHMENT_BYTES)}
              {overLimit && ' — remove some attachments to send'}
            </div>
          </div>
        )}
        {error && (
          <div role="alert" className="error-banner">
            {error}
          </div>
        )}
      </div>
      <footer className="mail-compose-foot">
        <p className="mail-compose-policy">{PHI_POLICY_LINE}</p>
        <div className="mail-compose-actions">
          <button type="button" className="btn btn-primary" disabled={sending} onClick={() => void send()}>
            {sending ? 'Sending…' : 'Send'}
          </button>
          <input ref={fileInput} type="file" multiple hidden aria-label="Attach files" onChange={pickFiles} />
          <button type="button" className="btn" disabled={sending} onClick={() => fileInput.current?.click()}>
            <Paperclip size={14} aria-hidden /> Attach
          </button>
          <button type="button" className="btn" disabled={sending} onClick={discard}>
            Discard
          </button>
          <span className="cell-sub mail-compose-limit">Up to {MAX_RECIPIENTS} recipients</span>
        </div>
      </footer>
    </section>
  );
}
